import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Layout from "../../components/Layout/Layout";
import Loading from "../../components/Loading";
import CustomModal from "../../components/Modal";
import {
  acceptCustomer,
  getNonVerifiedCustomers,
  rejectCustomer,
} from "../../utils/api";

const NonVerifiedCustomer = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [verifyModal, setVerifyModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [actionType, setActionType] = useState(null); // 'verify' or 'reject'

  const fetchNonVerifiedCustomers = async () => {
    setLoading(true);
    try {
      const [success, data] = await getNonVerifiedCustomers();
      if (success) {
        setCustomers(data);
      } else {
        toast.error("Failed to fetch non-verified customers");
      }
    } catch (error) {
      toast.error("An error occurred while fetching data");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchNonVerifiedCustomers();
  }, []);

  const handleAcceptClick = (customer) => {
    setSelectedCustomer(customer);
    setActionType("verify");
    setVerifyModal(true);
  };

  const handleRejectClick = (customer) => {
    setSelectedCustomer(customer);
    setActionType("reject");
    setVerifyModal(true);
  };

  const handleConfirmAction = async () => {
    if (!selectedCustomer) return;

    setLoading(true);
    try {
      const apiCall = actionType === "verify" ? acceptCustomer : rejectCustomer;
      const [success] = await apiCall(selectedCustomer._id);

      if (success) {
        const actionText = actionType === "verify" ? "accepted" : "rejected";
        toast.success(`Customer ${actionText} successfully`);
        fetchNonVerifiedCustomers(); // Refresh the list
        setVerifyModal(false);
        setSelectedCustomer(null);
        setActionType(null);
      } else {
        toast.error("Failed to update customer");
      }
    } catch (error) {
      toast.error("An error occurred while updating customer");
    }
    setLoading(false);
  };

  const handleModalClose = () => {
    setVerifyModal(false);
    setSelectedCustomer(null);
    setActionType(null);
  };

  return (
    <Layout>
      {loading && <Loading />}

      <div className="p-4">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">
            Non-Verified Customers
          </h1>
          <button
            onClick={fetchNonVerifiedCustomers}
            className="bg-primary text-white px-4 py-2  hover:bg-primary-dark transition-colors"
          >
            Refresh
          </button>
        </div>

        <div className="bg-white   overflow-hidden">
          {customers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p className="text-lg">No non-verified customers found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phone Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Registration Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Group
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {customers.map((customer) => (
                    <tr key={customer._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {customer.userName || customer.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {customer.phoneNumber || "N/A"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {customer.email || "N/A"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {customer.createdAt
                            ? new Date(customer.createdAt).toLocaleDateString()
                            : "N/A"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {customer.group?.name || "N/A"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold  bg-yellow-50 border border-yellow-900 rounded-full text-yellow-900">
                          Non-Verified
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleAcceptClick(customer)}
                            className="bg-green-600 text-white px-3 py-1  text-sm hover:bg-green-700 transition-colors"
                          >
                            Accept
                          </button>
                          <button
                            onClick={() => handleRejectClick(customer)}
                            className="bg-red-600 text-white px-3 py-1  text-sm hover:bg-red-700 transition-colors"
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Accept Modal */}
        <CustomModal
          open={verifyModal && actionType === "verify"}
          setOpen={handleModalClose}
          title="Accept Customer"
          button={
            <div className="flex gap-3">
              <button
                onClick={handleConfirmAction}
                disabled={loading}
                className="bg-green-600 text-white px-4 py-2  hover:bg-green-700 disabled:opacity-50"
              >
                {loading ? "Processing..." : "Yes, Accept"}
              </button>
              <button
                onClick={handleModalClose}
                className="bg-gray-500 text-white px-4 py-2  hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          }
        >
          <div className="p-4">
            <p className="text-lg mb-4">
              Are you sure you want to accept customer{" "}
              <span className="font-semibold">
                {selectedCustomer?.userName || selectedCustomer?.name}
              </span>
              ?
            </p>
            <p className="text-gray-600">
              This action will mark the customer as accepted and allow them to
              access the system.
            </p>
          </div>
        </CustomModal>

        <CustomModal
          open={verifyModal && actionType === "reject"}
          setOpen={handleModalClose}
          title="Reject Customer"
          button={
            <div className="flex gap-3">
              <button
                onClick={handleConfirmAction}
                disabled={loading}
                className="bg-red-600 text-white px-4 py-2  hover:bg-red-700 disabled:opacity-50"
              >
                {loading ? "Processing..." : "Yes, Reject"}
              </button>
              <button
                onClick={handleModalClose}
                className="bg-gray-500 text-white px-4 py-2  hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          }
        >
          <div className="p-4">
            <p className="text-lg mb-4">
              Are you sure you want to reject customer{" "}
              <span className="font-semibold">
                {selectedCustomer?.userName || selectedCustomer?.name}
              </span>
              ?
            </p>
            <p className="text-gray-600">
              This action will mark the customer as rejected and prevent them
              from accessing the system.
            </p>
          </div>
        </CustomModal>
      </div>
    </Layout>
  );
};

export default NonVerifiedCustomer;
